import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2, Save, Edit3, X, Upload, ChevronDown, ChevronUp, GripVertical } from 'lucide-react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminCourseManager({ user }) {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [exercises, setExercises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingCourse, setEditingCourse] = useState(false);
  const [courseForm, setCourseForm] = useState({});
  const [editingExerciseId, setEditingExerciseId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [showBulkAdd, setShowBulkAdd] = useState(false);
  const [bulkExercises, setBulkExercises] = useState([createEmptyExercise()]);
  const [uploadingVideoFor, setUploadingVideoFor] = useState(null);
  const [expandedExercise, setExpandedExercise] = useState(null);

  function createEmptyExercise() {
    return {
      _key: Date.now() + Math.random(),
      title: '',
      description: '',
      youtube_url: '',
      video_file: '',
      category: '',
      difficulty: 'beginner',
      body_parts: '',
      duration_minutes: 15,
      videoSource: 'youtube'
    };
  }

  useEffect(() => {
    fetchCourseData();
  }, [courseId]);

  const fetchCourseData = async () => {
    const token = localStorage.getItem('token');
    try {
      const [courseRes, exercisesRes] = await Promise.all([
        fetch(`${API}/courses/${courseId}`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API}/exercises`, { headers: { 'Authorization': `Bearer ${token}` } })
      ]);

      if (courseRes.ok) {
        const courseData = await courseRes.json();
        setCourse(courseData);
        setCourseForm({
          title: courseData.title,
          description: courseData.description,
          price: courseData.price,
          category: courseData.category,
          difficulty: courseData.difficulty,
          duration_weeks: courseData.duration_weeks,
          is_subscription: courseData.is_subscription || false
        });

        if (exercisesRes.ok) {
          const allExercises = await exercisesRes.json();
          const courseExercises = allExercises.filter(ex => courseData.exercise_ids?.includes(ex.id));
          setExercises(courseExercises);
        }
      } else {
        toast.error('Course not found');
        navigate('/admin');
      }
    } catch (error) {
      toast.error('Failed to load course');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveCourse = async () => {
    setSaving(true);
    const token = localStorage.getItem('token');
    try {
      const payload = {
        ...courseForm,
        exercise_ids: course.exercise_ids || []
      };
      const res = await fetch(`${API}/courses/${courseId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(payload)
      });
      if (res.ok) {
        toast.success('Course details saved');
        setCourse(prev => ({ ...prev, ...courseForm }));
        setEditingCourse(false);
      } else {
        toast.error('Failed to save course');
      }
    } catch (error) {
      toast.error('Failed to save course');
    } finally {
      setSaving(false);
    }
  };

  const handleVideoUpload = async (file, callback) => {
    const token = localStorage.getItem('token');
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await fetch(`${API}/upload/video`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      if (res.ok) {
        const data = await res.json();
        callback(data.filename);
        toast.success('Video uploaded');
      } else {
        toast.error('Upload failed');
      }
    } catch (error) {
      toast.error('Upload failed');
    }
  };

  const handleBulkVideoUpload = async (file, index) => {
    setUploadingVideoFor(index);
    await handleVideoUpload(file, (filename) => {
      setBulkExercises(prev => prev.map((ex, i) =>
        i === index ? { ...ex, video_file: filename, youtube_url: '' } : ex
      ));
    });
    setUploadingVideoFor(null);
  };

  const handleBulkAdd = async () => {
    const valid = bulkExercises.filter(ex => ex.title.trim());
    if (valid.length === 0) {
      toast.error('Add at least one exercise with a title');
      return;
    }

    setSaving(true);
    const token = localStorage.getItem('token');

    try {
      const exercisePayloads = valid.map(ex => ({
        title: ex.title,
        description: ex.description || '',
        youtube_url: ex.youtube_url || '',
        video_file: ex.video_file || '',
        category: ex.category || course.category || 'general',
        difficulty: ex.difficulty || 'beginner',
        body_parts: typeof ex.body_parts === 'string'
          ? ex.body_parts.split(',').map(s => s.trim()).filter(Boolean)
          : ex.body_parts || [],
        duration_minutes: parseInt(ex.duration_minutes) || 15
      }));

      const res = await fetch(`${API}/exercises/bulk`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(exercisePayloads)
      });

      if (res.ok) {
        const data = await res.json();
        const newIds = data.created.map(e => e.id);
        const updatedExerciseIds = [...(course.exercise_ids || []), ...newIds];

        await fetch(`${API}/courses/${courseId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({
            ...courseForm,
            exercise_ids: updatedExerciseIds
          })
        });

        toast.success(`${data.count} exercise(s) added to course`);
        setShowBulkAdd(false);
        setBulkExercises([createEmptyExercise()]);
        setCourse(prev => ({ ...prev, exercise_ids: updatedExerciseIds }));
        fetchCourseData();
      } else {
        toast.error('Failed to create exercises');
      }
    } catch (error) {
      toast.error('Failed to add exercises');
    } finally {
      setSaving(false);
    }
  };

  const handleEditExercise = (exercise) => {
    setEditingExerciseId(exercise.id);
    setEditForm({
      title: exercise.title,
      description: exercise.description,
      youtube_url: exercise.youtube_url || '',
      video_file: exercise.video_file || '',
      category: exercise.category,
      difficulty: exercise.difficulty,
      body_parts: Array.isArray(exercise.body_parts) ? exercise.body_parts.join(', ') : exercise.body_parts || '',
      duration_minutes: exercise.duration_minutes,
      videoSource: exercise.youtube_url ? 'youtube' : 'upload'
    });
  };

  const handleSaveExercise = async () => {
    setSaving(true);
    const token = localStorage.getItem('token');
    try {
      const payload = {
        title: editForm.title,
        description: editForm.description,
        youtube_url: editForm.youtube_url || '',
        video_file: editForm.video_file || '',
        category: editForm.category,
        difficulty: editForm.difficulty,
        body_parts: typeof editForm.body_parts === 'string'
          ? editForm.body_parts.split(',').map(s => s.trim()).filter(Boolean)
          : editForm.body_parts,
        duration_minutes: parseInt(editForm.duration_minutes) || 15
      };

      const res = await fetch(`${API}/exercises/${editingExerciseId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        toast.success('Exercise updated');
        setEditingExerciseId(null);
        fetchCourseData();
      } else {
        toast.error('Failed to update exercise');
      }
    } catch (error) {
      toast.error('Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveExercise = async (exerciseId) => {
    if (!window.confirm('Remove this exercise from the course?')) return;
    const token = localStorage.getItem('token');
    const updatedIds = course.exercise_ids.filter(id => id !== exerciseId);

    try {
      await fetch(`${API}/courses/${courseId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ ...courseForm, exercise_ids: updatedIds })
      });

      await fetch(`${API}/exercises/${exerciseId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      toast.success('Exercise removed');
      setCourse(prev => ({ ...prev, exercise_ids: updatedIds }));
      setExercises(prev => prev.filter(ex => ex.id !== exerciseId));
    } catch (error) {
      toast.error('Failed to remove exercise');
    }
  };

  const handleEditVideoUpload = async (file) => {
    setUploadingVideoFor('edit');
    await handleVideoUpload(file, (filename) => {
      setEditForm(prev => ({ ...prev, video_file: filename, youtube_url: '' }));
    });
    setUploadingVideoFor(null);
  };

  const handleDragEnd = async (result) => {
    if (!result.destination) return;
    const from = result.source.index;
    const to = result.destination.index;
    if (from === to) return;

    const reordered = Array.from(exercises);
    const [moved] = reordered.splice(from, 1);
    reordered.splice(to, 0, moved);
    setExercises(reordered);

    const newIds = reordered.map(ex => ex.id);
    setCourse(prev => ({ ...prev, exercise_ids: newIds }));

    const token = localStorage.getItem('token');
    try {
      await fetch(`${API}/courses/${courseId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ ...courseForm, exercise_ids: newIds })
      });
      toast.success('Exercise order saved');
    } catch (error) {
      toast.error('Failed to save order');
      fetchCourseData();
    }
  };

  if (loading) {
    return <div className="min-h-screen bg-[#F4EFE6] flex items-center justify-center text-[#0B0B0B] text-xl">Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-[#F4EFE6]">
      <nav className="bg-[#0B0B0B] text-white px-6 py-4">
        <div className="container mx-auto flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Button data-testid="back-to-admin-btn" onClick={() => navigate('/admin')} variant="ghost" className="text-white hover:bg-white/10 rounded-full">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back
            </Button>
            <h1 className="text-xl font-bold" style={{ fontFamily: 'Clash Display' }}>Course Manager</h1>
          </div>
        </div>
      </nav>

      <div className="container mx-auto px-4 sm:px-6 py-8 max-w-5xl">
        {/* Course Details Card */}
        <Card className="p-6 bg-white rounded-2xl mb-8" data-testid="course-details-card">
          <div className="flex justify-between items-start mb-4">
            <h2 className="text-2xl font-bold text-[#0B0B0B]" style={{ fontFamily: 'Clash Display' }}>Course Details</h2>
            {!editingCourse ? (
              <Button data-testid="edit-course-btn" onClick={() => setEditingCourse(true)} variant="outline" className="rounded-full" size="sm">
                <Edit3 className="w-4 h-4 mr-1" /> Edit
              </Button>
            ) : (
              <div className="flex gap-2">
                <Button data-testid="cancel-edit-course-btn" onClick={() => { setEditingCourse(false); setCourseForm({ title: course.title, description: course.description, price: course.price, category: course.category, difficulty: course.difficulty, duration_weeks: course.duration_weeks, is_subscription: course.is_subscription }); }} variant="outline" className="rounded-full" size="sm">
                  <X className="w-4 h-4 mr-1" /> Cancel
                </Button>
                <Button data-testid="save-course-btn" onClick={handleSaveCourse} disabled={saving} className="bg-[#0B0B0B] text-white rounded-full" size="sm">
                  <Save className="w-4 h-4 mr-1" /> {saving ? 'Saving...' : 'Save'}
                </Button>
              </div>
            )}
          </div>

          {editingCourse ? (
            <div className="grid md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Label>Title</Label>
                <Input data-testid="course-title-edit" value={courseForm.title} onChange={(e) => setCourseForm(prev => ({ ...prev, title: e.target.value }))} />
              </div>
              <div className="md:col-span-2">
                <Label>Description</Label>
                <Textarea data-testid="course-desc-edit" value={courseForm.description} onChange={(e) => setCourseForm(prev => ({ ...prev, description: e.target.value }))} rows={3} />
              </div>
              <div>
                <Label>Price (ZAR)</Label>
                <Input data-testid="course-price-edit" type="number" step="0.01" value={courseForm.price} onChange={(e) => setCourseForm(prev => ({ ...prev, price: parseFloat(e.target.value) || 0 }))} />
              </div>
              <div>
                <Label>Category</Label>
                <Input value={courseForm.category} onChange={(e) => setCourseForm(prev => ({ ...prev, category: e.target.value }))} />
              </div>
              <div>
                <Label>Difficulty</Label>
                <Select value={courseForm.difficulty} onValueChange={(val) => setCourseForm(prev => ({ ...prev, difficulty: val }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="beginner">Beginner</SelectItem>
                    <SelectItem value="intermediate">Intermediate</SelectItem>
                    <SelectItem value="advanced">Advanced</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Duration (weeks)</Label>
                <Input type="number" value={courseForm.duration_weeks} onChange={(e) => setCourseForm(prev => ({ ...prev, duration_weeks: parseInt(e.target.value) || 1 }))} />
              </div>
            </div>
          ) : (
            <div>
              <h3 className="text-xl font-bold text-[#0B0B0B] mb-1">{course.title}</h3>
              <p className="text-gray-600 mb-3">{course.description}</p>
              <div className="flex flex-wrap gap-3 text-sm">
                <span className="bg-[#D9C7A3] text-[#0B0B0B] px-3 py-1 rounded-full font-bold">R{course.price}</span>
                <span className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full capitalize">{course.difficulty}</span>
                <span className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full">{course.category}</span>
                <span className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full">{course.duration_weeks} weeks</span>
                <span className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full">{exercises.length} exercises</span>
              </div>
            </div>
          )}
        </Card>

        {/* Exercises Section */}
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-[#0B0B0B]" style={{ fontFamily: 'Clash Display' }}>
            Exercises ({exercises.length})
          </h2>
          <Button data-testid="bulk-add-btn" onClick={() => setShowBulkAdd(true)} className="bg-[#D9C7A3] text-[#0B0B0B] hover:bg-[#C6A97C] rounded-full">
            <Plus className="w-4 h-4 mr-2" /> Add Exercises
          </Button>
        </div>

        {exercises.length === 0 ? (
          <Card className="p-12 bg-white rounded-2xl text-center">
            <p className="text-gray-500 mb-4">No exercises in this course yet.</p>
            <Button data-testid="bulk-add-empty-btn" onClick={() => setShowBulkAdd(true)} className="bg-[#D9C7A3] text-[#0B0B0B] hover:bg-[#C6A97C] rounded-full">
              <Plus className="w-4 h-4 mr-2" /> Add Your First Exercises
            </Button>
          </Card>
        ) : (
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="exercises-list">
              {(provided) => (
                <div ref={provided.innerRef} {...provided.droppableProps} className="space-y-3">
                  {exercises.map((exercise, index) => (
                    <Draggable key={exercise.id} draggableId={exercise.id} index={index} isDragDisabled={editingExerciseId === exercise.id}>
                      {(provided, snapshot) => (
                        <div ref={provided.innerRef} {...provided.draggableProps} data-testid={`exercise-card-${index}`}>
                          <Card className={`bg-white rounded-2xl overflow-hidden transition-shadow ${snapshot.isDragging ? 'shadow-xl ring-2 ring-[#C6A97C]' : ''}`}>
                            {editingExerciseId === exercise.id ? (
                              <div className="p-5 space-y-4">
                                <div className="flex justify-between items-center">
                                  <h4 className="font-bold text-[#0B0B0B]">Edit Exercise</h4>
                                  <div className="flex gap-2">
                                    <Button size="sm" variant="outline" className="rounded-full" onClick={() => setEditingExerciseId(null)}>
                                      <X className="w-3 h-3 mr-1" /> Cancel
                                    </Button>
                                    <Button data-testid={`save-exercise-${index}`} size="sm" disabled={saving} onClick={handleSaveExercise} className="bg-[#0B0B0B] text-white rounded-full">
                                      <Save className="w-3 h-3 mr-1" /> {saving ? 'Saving...' : 'Save'}
                                    </Button>
                                  </div>
                                </div>
                                <div className="grid md:grid-cols-2 gap-3">
                                  <div className="md:col-span-2">
                                    <Label>Title</Label>
                                    <Input value={editForm.title} onChange={(e) => setEditForm(prev => ({ ...prev, title: e.target.value }))} />
                                  </div>
                                  <div className="md:col-span-2">
                                    <Label>Description</Label>
                                    <Textarea value={editForm.description} onChange={(e) => setEditForm(prev => ({ ...prev, description: e.target.value }))} rows={2} />
                                  </div>
                                  <div>
                                    <Label>Video Source</Label>
                                    <div className="flex gap-3 mt-1">
                                      <label className="flex items-center gap-1.5 cursor-pointer text-sm">
                                        <input type="radio" checked={editForm.videoSource === 'youtube'} onChange={() => setEditForm(prev => ({ ...prev, videoSource: 'youtube', video_file: '' }))} />
                                        YouTube
                                      </label>
                                      <label className="flex items-center gap-1.5 cursor-pointer text-sm">
                                        <input type="radio" checked={editForm.videoSource === 'upload'} onChange={() => setEditForm(prev => ({ ...prev, videoSource: 'upload', youtube_url: '' }))} />
                                        Upload
                                      </label>
                                    </div>
                                  </div>
                                  <div>
                                    {editForm.videoSource === 'youtube' ? (
                                      <>
                                        <Label>YouTube URL</Label>
                                        <Input value={editForm.youtube_url} onChange={(e) => setEditForm(prev => ({ ...prev, youtube_url: e.target.value, video_file: '' }))} placeholder="https://youtube.com/watch?v=..." />
                                      </>
                                    ) : (
                                      <>
                                        <Label>Video File</Label>
                                        <input type="file" accept="video/*" onChange={(e) => e.target.files[0] && handleEditVideoUpload(e.target.files[0])} disabled={uploadingVideoFor === 'edit'} className="w-full p-1.5 border rounded text-sm" />
                                        {uploadingVideoFor === 'edit' && <p className="text-xs text-gray-500 mt-1">Uploading...</p>}
                                        {editForm.video_file && <p className="text-xs text-green-600 mt-1">Uploaded: {editForm.video_file}</p>}
                                      </>
                                    )}
                                  </div>
                                  <div>
                                    <Label>Body Parts</Label>
                                    <Input value={editForm.body_parts} onChange={(e) => setEditForm(prev => ({ ...prev, body_parts: e.target.value }))} placeholder="knee, hip, back" />
                                  </div>
                                  <div>
                                    <Label>Difficulty</Label>
                                    <Select value={editForm.difficulty} onValueChange={(val) => setEditForm(prev => ({ ...prev, difficulty: val }))}>
                                      <SelectTrigger><SelectValue /></SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value="beginner">Beginner</SelectItem>
                                        <SelectItem value="intermediate">Intermediate</SelectItem>
                                        <SelectItem value="advanced">Advanced</SelectItem>
                                      </SelectContent>
                                    </Select>
                                  </div>
                                  <div>
                                    <Label>Category</Label>
                                    <Input value={editForm.category} onChange={(e) => setEditForm(prev => ({ ...prev, category: e.target.value }))} />
                                  </div>
                                  <div>
                                    <Label>Duration (min)</Label>
                                    <Input type="number" value={editForm.duration_minutes} onChange={(e) => setEditForm(prev => ({ ...prev, duration_minutes: parseInt(e.target.value) || 15 }))} />
                                  </div>
                                </div>
                              </div>
                            ) : (
                              <div className="p-5">
                                <div className="flex items-start justify-between gap-3">
                                  <div {...provided.dragHandleProps} className="flex items-center pt-1 cursor-grab active:cursor-grabbing" data-testid={`drag-handle-${index}`}>
                                    <GripVertical className="w-5 h-5 text-gray-300 hover:text-gray-500 transition-colors" />
                                  </div>
                                  <div className="flex items-start gap-3 flex-1 cursor-pointer" onClick={() => setExpandedExercise(expandedExercise === exercise.id ? null : exercise.id)}>
                                    <div className="w-8 h-8 rounded-full bg-[#D9C7A3] flex items-center justify-center flex-shrink-0 text-[#0B0B0B] font-bold text-sm">
                                      {index + 1}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <h4 className="font-bold text-[#0B0B0B]">{exercise.title}</h4>
                                      <div className="flex flex-wrap gap-2 mt-1 text-xs text-gray-500">
                                        <span>{exercise.duration_minutes} min</span>
                                        <span className="capitalize">{exercise.difficulty}</span>
                                        {exercise.body_parts?.length > 0 && <span>{exercise.body_parts.join(', ')}</span>}
                                        {exercise.youtube_url && <span className="text-red-500">YouTube</span>}
                                        {exercise.video_file && <span className="text-blue-500">Video File</span>}
                                      </div>
                                    </div>
                                    {expandedExercise === exercise.id ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                                  </div>
                                  <div className="flex gap-1.5 flex-shrink-0">
                                    <Button data-testid={`edit-exercise-${index}`} size="sm" variant="outline" className="rounded-full h-8 w-8 p-0" onClick={() => handleEditExercise(exercise)}>
                                      <Edit3 className="w-3.5 h-3.5" />
                                    </Button>
                                    <Button data-testid={`remove-exercise-${index}`} size="sm" variant="outline" className="rounded-full h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50" onClick={() => handleRemoveExercise(exercise.id)}>
                                      <Trash2 className="w-3.5 h-3.5" />
                                    </Button>
                                  </div>
                                </div>
                                {expandedExercise === exercise.id && (
                                  <div className="mt-3 pl-11 text-sm text-gray-600 border-t pt-3">
                                    <p>{exercise.description}</p>
                                    {exercise.category && <p className="mt-1"><span className="font-medium text-gray-700">Category:</span> {exercise.category}</p>}
                                  </div>
                                )}
                              </div>
                            )}
                          </Card>
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        )}
      </div>

      {/* Bulk Add Dialog */}
      <Dialog open={showBulkAdd} onOpenChange={setShowBulkAdd}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="bulk-add-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-[#0B0B0B]" style={{ fontFamily: 'Clash Display' }}>
              Add Exercises to {course?.title}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6 mt-2">
            {bulkExercises.map((ex, index) => (
              <Card key={ex._key} className="p-4 border-2 border-dashed border-gray-200" data-testid={`bulk-exercise-${index}`}>
                <div className="flex justify-between items-center mb-3">
                  <span className="text-sm font-bold text-[#0B0B0B]">Exercise {index + 1}</span>
                  {bulkExercises.length > 1 && (
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-400 hover:text-red-600" onClick={() => setBulkExercises(prev => prev.filter((_, i) => i !== index))}>
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                </div>
                <div className="grid md:grid-cols-2 gap-3">
                  <div className="md:col-span-2">
                    <Label>Title *</Label>
                    <Input data-testid={`bulk-title-${index}`} value={ex.title} onChange={(e) => setBulkExercises(prev => prev.map((item, i) => i === index ? { ...item, title: e.target.value } : item))} placeholder="e.g., Knee Extension Stretch" />
                  </div>
                  <div className="md:col-span-2">
                    <Label>Description</Label>
                    <Textarea value={ex.description} onChange={(e) => setBulkExercises(prev => prev.map((item, i) => i === index ? { ...item, description: e.target.value } : item))} rows={2} placeholder="Describe the exercise..." />
                  </div>
                  <div>
                    <Label>Video</Label>
                    <div className="flex gap-2 mt-1 mb-2">
                      <label className="flex items-center gap-1 cursor-pointer text-xs">
                        <input type="radio" checked={ex.videoSource === 'youtube'} onChange={() => setBulkExercises(prev => prev.map((item, i) => i === index ? { ...item, videoSource: 'youtube', video_file: '' } : item))} />
                        YouTube
                      </label>
                      <label className="flex items-center gap-1 cursor-pointer text-xs">
                        <input type="radio" checked={ex.videoSource === 'upload'} onChange={() => setBulkExercises(prev => prev.map((item, i) => i === index ? { ...item, videoSource: 'upload', youtube_url: '' } : item))} />
                        Upload
                      </label>
                    </div>
                    {ex.videoSource === 'youtube' ? (
                      <Input value={ex.youtube_url} onChange={(e) => setBulkExercises(prev => prev.map((item, i) => i === index ? { ...item, youtube_url: e.target.value, video_file: '' } : item))} placeholder="https://youtube.com/watch?v=..." />
                    ) : (
                      <>
                        <input type="file" accept="video/*" onChange={(e) => e.target.files[0] && handleBulkVideoUpload(e.target.files[0], index)} disabled={uploadingVideoFor === index} className="w-full p-1.5 border rounded text-sm" />
                        {uploadingVideoFor === index && <p className="text-xs text-gray-500 mt-1">Uploading...</p>}
                        {ex.video_file && <p className="text-xs text-green-600 mt-1">Uploaded: {ex.video_file}</p>}
                      </>
                    )}
                  </div>
                  <div>
                    <Label>Body Parts</Label>
                    <Input value={ex.body_parts} onChange={(e) => setBulkExercises(prev => prev.map((item, i) => i === index ? { ...item, body_parts: e.target.value } : item))} placeholder="knee, hip, back" className="mt-1" />
                  </div>
                  <div>
                    <Label>Difficulty</Label>
                    <Select value={ex.difficulty} onValueChange={(val) => setBulkExercises(prev => prev.map((item, i) => i === index ? { ...item, difficulty: val } : item))}>
                      <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="beginner">Beginner</SelectItem>
                        <SelectItem value="intermediate">Intermediate</SelectItem>
                        <SelectItem value="advanced">Advanced</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Duration (min)</Label>
                    <Input type="number" value={ex.duration_minutes} onChange={(e) => setBulkExercises(prev => prev.map((item, i) => i === index ? { ...item, duration_minutes: e.target.value } : item))} className="mt-1" />
                  </div>
                </div>
              </Card>
            ))}

            <Button data-testid="add-another-exercise-btn" onClick={() => setBulkExercises(prev => [...prev, createEmptyExercise()])} variant="outline" className="w-full border-dashed border-2 rounded-xl py-6 text-gray-500 hover:text-[#0B0B0B] hover:border-[#0B0B0B]">
              <Plus className="w-4 h-4 mr-2" /> Add Another Exercise
            </Button>

            <Button data-testid="save-bulk-exercises-btn" onClick={handleBulkAdd} disabled={saving} className="w-full bg-[#0B0B0B] text-white rounded-full py-6 text-lg font-bold">
              {saving ? 'Creating...' : `Save ${bulkExercises.filter(e => e.title.trim()).length} Exercise(s) to Course`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
